import type { TokenPair } from '@/gen/tagn/v1/auth_pb';
import type { User } from '@/gen/tagn/v1/common_pb';

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
}

export interface Session {
  user: SessionUser;
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
  refreshExpiresAt: number;
}

const KEY = 'tagn.session';

type Listener = (s: Session | null) => void;

/** Persists the session in localStorage and notifies subscribers. */
export class SessionStore {
  private session: Session | null;
  private readonly listeners = new Set<Listener>();

  constructor(private readonly storage: Storage = localStorage) {
    this.session = SessionStore.read(storage);
  }

  private static read(storage: Storage): Session | null {
    try {
      const raw = storage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  }

  get(): Session | null {
    return this.session;
  }

  set(session: Session | null): void {
    this.session = session;
    try {
      if (session) this.storage.setItem(KEY, JSON.stringify(session));
      else this.storage.removeItem(KEY);
    } catch {
      // storage unavailable (private mode) – keep the in-memory session
    }
    this.listeners.forEach((l) => l(session));
  }

  /** Updates the token pair while keeping the user. */
  setTokens(tokens: TokenPair): Session | null {
    if (!this.session) return null;
    const next = { ...this.session, ...tokensToSession(tokens) };
    this.set(next);
    return next;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export function tokensToSession(t: TokenPair): Omit<Session, 'user'> {
  return {
    accessToken: t.accessToken,
    accessExpiresAt: Number(t.accessExpiresAtMs),
    refreshToken: t.refreshToken,
    refreshExpiresAt: Number(t.refreshExpiresAtMs),
  };
}

export function userFromProto(u: User | undefined): SessionUser {
  return { id: u?.id ?? '', email: u?.email ?? '', displayName: u?.displayName ?? '' };
}
